package top.wcpe.allincore.layout.engine

import kotlin.concurrent.Volatile
import kotlin.jvm.JvmStatic

/**
 * UI 子系统性能指标汇聚点（批 D / D1）。
 *
 * 渲染编排器与布局引擎在每帧热路径上把「帧渲染耗时 / 布局计算耗时 / 本帧布局节点数」
 * 写入这里；dev-debug 性能面板在另一线程读取最近值展示。本类不依赖 dev-debug，
 * 由 ui-runtime / layout-engine 自带，保证生产环境无须 dev-debug 也能编译运行。
 *
 * ## 生产零开销守卫
 * [isEnabled] 默认 `false`；仅 dev-debug 启用调试浮层时置 `true`。
 * 关闭状态下 `record*` 方法第一条语句即返回，整体开销仅一次 `volatile` 读。
 * 埋点侧约定 `long s = isEnabled() ? System.nanoTime() : 0L`，关闭时连 `nanoTime`
 * 都不调用。
 *
 * ## 并发模型
 * 每个指标字段单写者（客户端渲染线程，每帧顺序写）+ 单读者（dev tick 桥接），
 * 用 `volatile` 保证可见性即可，无需加锁、无分配、不阻塞。脏区不可得时
 * 以「本帧布局节点总数」降级近似（见 [recordDirtyNodes]）。
 */
object UiPerfSink {

    /** 全局启用开关：仅 dev-debug 浮层激活时为 `true`，生产默认 `false`。 */
    @Volatile
    private var enabled = false

    /** 最近一帧 UI 渲染耗时（纳秒）。 */
    @Volatile
    private var lastFrameRenderNs = 0L

    /** 最近一次布局计算耗时（纳秒）。 */
    @Volatile
    private var lastLayoutComputeNs = 0L

    /**
     * 最近一次布局产出的节点总数。
     *
     * 当前布局引擎每帧整树重算、渲染每帧整树遍历，不存在增量「脏区」信息，
     * 故此值为「本帧参与布局的节点数」降级近似，用于直观反映 UI 规模与重算量。
     */
    @Volatile
    private var lastDirtyNodeCount = 0

    /** 是否启用埋点；热路径据此短路。 */
    @JvmStatic
    fun isEnabled(): Boolean {
        return enabled
    }

    /**
     * 设置启用开关。
     *
     * 由 dev-debug 启用 / 关闭调试浮层时调用；关闭时不清空最近值，
     * 保留最后一帧快照便于面板读取「停采前最后状态」。
     */
    @JvmStatic
    fun setEnabled(value: Boolean) {
        enabled = value
    }

    /** 记录一帧 UI 渲染耗时（纳秒）；未启用时直接返回。 */
    @JvmStatic
    fun recordRender(durationNs: Long) {
        if (!enabled) {
            return
        }
        lastFrameRenderNs = durationNs
    }

    /** 记录一次布局计算耗时（纳秒）；未启用时直接返回。 */
    @JvmStatic
    fun recordLayout(durationNs: Long) {
        if (!enabled) {
            return
        }
        lastLayoutComputeNs = durationNs
    }

    /** 记录本帧布局节点总数（脏区降级近似）；未启用时直接返回。 */
    @JvmStatic
    fun recordDirtyNodes(nodeCount: Int) {
        if (!enabled) {
            return
        }
        lastDirtyNodeCount = nodeCount
    }

    /** 读取最近一帧 UI 渲染耗时（纳秒）。 */
    @JvmStatic
    fun lastFrameRenderNs(): Long {
        return lastFrameRenderNs
    }

    /** 读取最近一次布局计算耗时（纳秒）。 */
    @JvmStatic
    fun lastLayoutComputeNs(): Long {
        return lastLayoutComputeNs
    }

    /** 读取最近一次布局节点总数。 */
    @JvmStatic
    fun lastDirtyNodeCount(): Int {
        return lastDirtyNodeCount
    }

    /** 复位所有最近值与开关；仅供测试与显式重置使用。 */
    @JvmStatic
    fun reset() {
        enabled = false
        lastFrameRenderNs = 0L
        lastLayoutComputeNs = 0L
        lastDirtyNodeCount = 0
    }
}
